/**
 * Mobile-first scoring.
 *
 * Designed for a phone in one hand on the 7th tee: one hole at a time, big
 * touch targets, the whole four-ball scored on one screen, shots-received shown
 * per player so the net number is never a mystery, and optional putts/fairway/GIR
 * capture for players who want the stats. Everything posts in one request per hole.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import type { ScorecardPayload } from '@fairwaylive/shared';
import { AdProvider } from '../lib/adClient.tsx';
import { AdZone, InterstitialAd } from '../components/AdZone.tsx';
import { ErrorNote, Section, Spinner } from '../components/Shell.tsx';
import { api, getToken, post } from '../lib/api.ts';
import { useFetch } from '../lib/useLive.ts';

interface GroupPlayer {
  entryId: string;
  displayName: string;
  playingHandicap: number;
  thru: number;
}

interface HoleEntry {
  strokes: number | null;
  putts?: number;
  fairwayHit?: boolean;
  greenInRegulation?: boolean;
  pickedUp?: boolean;
}

/**
 * Demo-mode scoring sandbox. The same component drives live scoring when an
 * organizer is signed in — it just posts to the tenant endpoint instead.
 */
export function ScoringPage() {
  const { tournamentId } = useParams<{ tournamentId?: string }>();
  const [params] = useSearchParams();
  const orgId = params.get('org');
  const isDemo = !tournamentId || tournamentId === 'demo';

  const entries = useFetch<{ entries: GroupPlayer[] }>(isDemo ? '/api/demo/entries' : null);

  if (isDemo && entries.loading) return <Spinner label="Loading the field" />;

  return (
    <AdProvider surface="scorecard" tournamentId={isDemo ? 'demo-tournament' : (tournamentId ?? null)} isDemo={isDemo}>
      <ScoringBody
        players={(entries.data?.entries ?? []).slice(0, 4)}
        tournamentId={tournamentId ?? 'demo'}
        orgId={orgId}
        isDemo={isDemo}
      />
      <InterstitialAd />
    </AdProvider>
  );
}

function ScoringBody({
  players,
  tournamentId,
  orgId,
  isDemo,
}: {
  players: GroupPlayer[];
  tournamentId: string;
  orgId: string | null;
  isDemo: boolean;
}) {
  const [hole, setHole] = useState(1);
  const [entries, setEntries] = useState<Record<string, HoleEntry>>({});
  const [detailed, setDetailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [card, setCard] = useState<ScorecardPayload | null>(null);

  const primary = players[0];

  // Load the leading player's card so par, stroke index and shots are real.
  useEffect(() => {
    if (!primary) return;
    const path = isDemo
      ? `/api/demo/scorecard/${primary.entryId}`
      : `/api/tournaments/${tournamentId}/scorecard/${primary.entryId}`;
    api<ScorecardPayload>(path)
      .then(setCard)
      .catch((err: Error) => setError(err.message));
  }, [isDemo, primary, tournamentId]);

  const holeInfo = useMemo(() => card?.holes.find((h) => h.holeNumber === hole) ?? null, [card, hole]);

  const setStrokes = useCallback((entryId: string, strokes: number | null) => {
    setEntries((current) => ({ ...current, [entryId]: { ...current[entryId], strokes } }));
  }, []);

  const setField = useCallback((entryId: string, patch: Partial<HoleEntry>) => {
    setEntries((current) => ({ ...current, [entryId]: { ...current[entryId], ...patch } }));
  }, []);

  const save = async () => {
    const cards = players
      .map((player) => ({ entryId: player.entryId, entry: entries[player.entryId] }))
      .filter((row) => row.entry?.strokes || row.entry?.pickedUp)
      .map((row) => ({
        entryId: row.entryId,
        holes: [
          {
            holeNumber: hole,
            strokes: row.entry?.strokes ?? null,
            ...(row.entry?.putts === undefined ? {} : { putts: row.entry.putts }),
            ...(row.entry?.fairwayHit === undefined ? {} : { fairwayHit: row.entry.fairwayHit }),
            ...(row.entry?.greenInRegulation === undefined ? {} : { greenInRegulation: row.entry.greenInRegulation }),
            ...(row.entry?.pickedUp ? { pickedUp: true } : {}),
          },
        ],
      }));

    if (!cards.length) {
      setError('Enter at least one score before saving.');
      return;
    }

    if (isDemo || !orgId) {
      // The demo simulator owns its own scores, so this is a local dry run.
      setMessage(
        `Hole ${hole} captured for ${cards.length} player${cards.length === 1 ? '' : 's'}. In a live event this posts to the tournament and every board recomputes immediately.`,
      );
      setEntries({});
      setHole((h) => Math.min(18, h + 1));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const result = await post<{ written: number; boards: Array<{ name: string }> }>(
        `/api/orgs/${orgId}/tournaments/${tournamentId}/scores`,
        { roundNumber: 1, cards, source: 'marker' },
      );
      setMessage(`Saved ${result.written} scores · ${result.boards.length} boards updated`);
      setEntries({});
      setHole((h) => Math.min(18, h + 1));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (!players.length) {
    return (
      <Section eyebrow="Scoring" title="Mobile scorecard">
        <ErrorNote
          message="No group loaded."
          hint={
            getToken()
              ? 'Open this from an event draw, or try the demo group at /score.'
              : 'The demo group needs the API running. Sign in to score a live event.'
          }
        />
      </Section>
    );
  }

  return (
    <div className="mx-auto max-w-[560px] px-3 pb-28 pt-4">
      <AdZone zoneKey="top_banner" />

      <div className="mt-4 flex items-center justify-between">
        <div>
          <p className="eyebrow">{isDemo ? 'Demo scoring — nothing is saved' : 'Live scoring'}</p>
          <h1 className="board-title text-2xl font-bold">
            Hole {hole}
            {holeInfo ? (
              <span className="ml-2 text-base font-normal text-[var(--color-muted)]">
                Par {holeInfo.par} · SI {holeInfo.strokeIndex}
                {holeInfo.yards ? ` · ${holeInfo.yards}m` : ''}
              </span>
            ) : null}
          </h1>
        </div>
        <Link to="/demo" className="btn btn-ghost !px-3 !py-1.5 !text-xs">
          Board
        </Link>
      </div>

      {/* Hole strip: tap to jump, filled dots show holes already captured. */}
      <div className="mt-3 flex gap-1 overflow-x-auto pb-1">
        {Array.from({ length: 18 }, (_, i) => i + 1).map((n) => {
          const played = (card?.holes.find((h) => h.holeNumber === n)?.strokes ?? null) !== null;
          return (
            <button
              key={n}
              type="button"
              onClick={() => setHole(n)}
              className={`tabular h-8 w-8 shrink-0 rounded-lg text-xs font-bold ${
                n === hole
                  ? 'bg-[var(--color-fairway-300)] text-[var(--color-fairway-950)]'
                  : played
                    ? 'bg-[var(--color-fairway-600)] text-[var(--color-chalk)]'
                    : 'bg-[var(--color-fairway-800)] text-[var(--color-muted-dim)]'
              }`}
            >
              {n}
            </button>
          );
        })}
      </div>

      {message ? (
        <div className="card mt-4 border-[var(--color-fairway-300)] p-3 text-[13px] text-[var(--color-fairway-300)]">{message}</div>
      ) : null}
      {error ? (
        <div className="mt-4">
          <ErrorNote message={error} />
        </div>
      ) : null}

      <div className="mt-4 space-y-3">
        {players.map((player) => {
          const entry = entries[player.entryId] ?? {};
          const shots = holeInfo
            ? Math.floor(player.playingHandicap / 18) + (holeInfo.strokeIndex <= player.playingHandicap % 18 ? 1 : 0)
            : 0;
          const par = holeInfo?.par ?? 4;
          return (
            <div key={player.entryId} className="card p-3">
              <div className="mb-2 flex items-baseline justify-between">
                <div>
                  <p className="font-semibold">{player.displayName}</p>
                  <p className="text-[11px] text-[var(--color-muted-dim)]">
                    Playing off {player.playingHandicap}
                    {shots > 0 ? ` · ${shots} shot${shots > 1 ? 's' : ''} here` : ' · no shot here'}
                  </p>
                </div>
                {entry.strokes ? (
                  <span className="tabular text-sm text-[var(--color-muted)]">
                    net {entry.strokes - shots} ({entry.strokes - shots - par >= 0 ? '+' : ''}
                    {entry.strokes - shots - par})
                  </span>
                ) : null}
              </div>

              {/* Quick-pick relative to par, then absolute numbers. */}
              <div className="flex flex-wrap gap-1.5">
                {[par - 2, par - 1, par, par + 1, par + 2, par + 3].map((value, i) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setStrokes(player.entryId, value)}
                    className={`tabular h-11 min-w-[44px] flex-1 rounded-lg text-base font-bold transition-colors ${
                      entry.strokes === value
                        ? 'bg-[var(--color-fairway-300)] text-[var(--color-fairway-950)]'
                        : 'bg-[var(--color-fairway-700)] text-[var(--color-chalk)] hover:bg-[var(--color-fairway-600)]'
                    }`}
                    aria-label={`${value} strokes for ${player.displayName}`}
                  >
                    {value}
                    <span className="ml-0.5 text-[9px] font-normal opacity-70">
                      {['alb', 'bird', 'par', 'bog', 'dbl', 'tpl'][i]}
                    </span>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setField(player.entryId, { pickedUp: !entry.pickedUp, strokes: null })}
                  className={`h-11 rounded-lg px-3 text-xs font-semibold ${
                    entry.pickedUp ? 'bg-[var(--color-rose-sun)] text-white' : 'bg-[var(--color-fairway-700)] text-[var(--color-muted)]'
                  }`}
                  title="Picked up / no return on this hole"
                >
                  NR
                </button>
              </div>

              {detailed ? (
                <div className="mt-2.5 flex flex-wrap items-center gap-2 text-[12px]">
                  <label className="flex items-center gap-1.5 text-[var(--color-muted)]">
                    Putts
                    <select
                      value={entry.putts ?? ''}
                      onChange={(event) => setField(player.entryId, { putts: Number(event.target.value) })}
                      className="rounded border border-[color-mix(in_oklab,var(--color-fairway-400)_30%,transparent)] bg-[var(--color-fairway-800)] px-2 py-1"
                    >
                      <option value="">–</option>
                      {[0, 1, 2, 3, 4, 5].map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </label>
                  {par !== 3 ? (
                    <Toggle
                      label="Fairway"
                      active={entry.fairwayHit === true}
                      onClick={() => setField(player.entryId, { fairwayHit: !entry.fairwayHit })}
                    />
                  ) : null}
                  <Toggle
                    label="GIR"
                    active={entry.greenInRegulation === true}
                    onClick={() => setField(player.entryId, { greenInRegulation: !entry.greenInRegulation })}
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <button type="button" onClick={() => setDetailed((d) => !d)} className="btn btn-ghost mt-3 w-full !text-xs">
        {detailed ? 'Hide putts, fairways and GIR' : 'Add putts, fairways and GIR'}
      </button>

      <div className="mt-6">
        <AdZone zoneKey="footer_banner" />
      </div>

      {/* Fixed action bar so saving is always a thumb away. */}
      <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-[color-mix(in_oklab,var(--color-fairway-400)_25%,transparent)] bg-[color-mix(in_oklab,var(--color-fairway-900)_96%,transparent)] px-3 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-[560px] gap-2">
          <button
            type="button"
            onClick={() => setHole((h) => Math.max(1, h - 1))}
            className="btn btn-ghost !px-4"
            disabled={hole === 1}
          >
            ←
          </button>
          <button type="button" onClick={() => void save()} disabled={saving} className="btn btn-primary flex-1 !py-3 !text-base">
            {saving ? 'Saving…' : `Save hole ${hole}`}
          </button>
          <button
            type="button"
            onClick={() => setHole((h) => Math.min(18, h + 1))}
            className="btn btn-ghost !px-4"
            disabled={hole === 18}
          >
            →
          </button>
        </div>
      </div>
    </div>
  );
}

function Toggle({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded px-2.5 py-1 font-semibold ${
        active ? 'bg-[var(--color-fairway-300)] text-[var(--color-fairway-950)]' : 'bg-[var(--color-fairway-700)] text-[var(--color-muted)]'
      }`}
    >
      {label}
    </button>
  );
}
